import type {
  IControlSettingData,
  IDynamicControlSetting,
} from '~/presentation/reports/common/type';
import type { EReportSource, EReportPresetKey } from '../types';

import { useControlSettingStore } from '~/presentation/reports/common/ReportFilter/stores';
import { EControlSettingType } from '~/presentation/reports/common/type';
import { useCampaignGroupStore } from '~/modules/campaign-group';
import { useParams, useSearchParams } from '@remix-run/react';
import { buildPreset } from '../utils/preset';
import { EReportParamKeys } from '../types';
import { useState, useEffect } from 'react';

type PresetType =
  | {
      presetData: IControlSettingData;
      presetUI: IDynamicControlSetting;
    }
  | undefined;
interface UsePresetProps {
  setControlData: (controlData: IControlSettingData) => void;
  initPreset?: PresetType;
  metricKey?: EReportPresetKey;
}

export const usePreset = ({ setControlData, initPreset, metricKey }: UsePresetProps) => {
  const resetControlSettingUI = useControlSettingStore((state) => state.resetControlSettingUI);
  const isQueryByCampaignGroups = useCampaignGroupStore((state) => state.isQueryByCampaignGroups);

  const { id } = useParams<{ id: EReportPresetKey }>();
  const [searchParams] = useSearchParams();

  const from = searchParams.get(EReportParamKeys.FROM) as EReportSource | null;
  const locationPath = searchParams.get(EReportParamKeys.PATH);
  const campaignId = searchParams.get(EReportParamKeys.CAMPAIGN_ID);
  const campaignType = searchParams.get(EReportParamKeys.CAMPAIGN_TYPE);
  const campaignGroupId = searchParams.get(EReportParamKeys.CAMPAIGN_GROUP_ID);

  const hasParams = Boolean(searchParams.toString().length > 0);

  const presetKey = id || metricKey;

  const [preset, setPreset] = useState<PresetType>(initPreset);

  useEffect(() => {
    if (!preset) return;

    setControlData(preset.presetData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(preset)]);

  useEffect(() => {
    if (!from || !presetKey || !isQueryByCampaignGroups) return;

    const data = buildPreset({
      from,
      presetKey,
      locationPath,
      campaign: {
        campaignId,
        campaignType,
        campaignGroupId,
      },
    });
    setPreset(data);
  }, [
    from,
    presetKey,
    locationPath,
    campaignId,
    campaignType,
    campaignGroupId,
    isQueryByCampaignGroups,
  ]);

  // Reset filters when "View all orders" button is clicked
  useEffect(() => {
    if (hasParams) return;

    setPreset(initPreset);
    initPreset?.presetData && setControlData(initPreset.presetData);
    resetControlSettingUI(EControlSettingType.ORDER);
  }, [hasParams, setControlData, resetControlSettingUI, initPreset]);

  return {
    preset,
  };
};
